import { useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '../../lib/utils';
import {
  applyDayClick,
  buildCalendarWeeks,
  daysInMonth,
  formatRangeLabel,
  isWithinRange,
  monthLabel,
  parseIso,
  shiftMonth,
  toIso,
} from '../../lib/dateRangePicker';

// A lightweight single-popover date-RANGE picker (Community inbox Part D2) —
// replaces the two separate From/To inputs. One calendar view; click a start
// day, then an end day (auto-swapped if you click earlier). Emits ISO
// 'yyyy-mm-dd' strings, matching the inbox filter shape. No date library.

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function todayIso(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export function DateRangePicker({
  from,
  to,
  onChange,
  ariaLabel = 'Date range',
}: {
  from: string | null | undefined;
  to: string | null | undefined;
  onChange: (from: string | undefined, to: string | undefined) => void;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  // The visible month defaults to the current start (or today).
  const initial = parseIso(from) ?? parseIso(todayIso())!;
  const [view, setView] = useState<{ y: number; m: number }>({ y: initial.y, m: initial.m });

  const f = from || null;
  const t = to || null;
  const weeks = buildCalendarWeeks(view.y, view.m);

  const onDayClick = (iso: string) => {
    const next = applyDayClick(iso, f, t);
    onChange(next.from, next.to ?? undefined);
  };

  const preset = (start: string, end: string) => {
    onChange(start, end);
    const p = parseIso(start)!;
    setView({ y: p.y, m: p.m });
  };
  const lastNDays = (n: number) => {
    const now = new Date();
    const startD = new Date(now);
    startD.setDate(now.getDate() - (n - 1));
    preset(
      toIso(startD.getFullYear(), startD.getMonth() + 1, startD.getDate()),
      toIso(now.getFullYear(), now.getMonth() + 1, now.getDate()),
    );
  };

  const hasValue = Boolean(f || t);

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            className={cn(
              'flex w-full items-center gap-2 rounded border border-slate-300 bg-white px-2 py-1.5 text-left text-sm hover:bg-slate-50',
              !hasValue && 'text-slate-500',
            )}
          >
            <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="min-w-0 flex-1 truncate">{formatRangeLabel(f, t)}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setView((v) => shiftMonth(v.y, v.m, -1))}
              className="rounded p-1 text-slate-500 hover:bg-slate-100"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium text-slate-700">{monthLabel(view.y, view.m)}</span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setView((v) => shiftMonth(v.y, v.m, 1))}
              className="rounded p-1 text-slate-500 hover:bg-slate-100"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="mb-1 grid grid-cols-7 gap-0.5">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="py-1 text-center text-[11px] font-medium text-slate-400">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {weeks.flat().map((cell) => {
              const inRange = isWithinRange(cell.iso, f, t);
              const isEndpoint = cell.iso === f || cell.iso === t;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  onClick={() => onDayClick(cell.iso)}
                  className={cn(
                    'h-8 w-8 rounded text-center text-xs tabular-nums transition-colors',
                    cell.inMonth ? 'text-slate-700' : 'text-slate-300',
                    isEndpoint
                      ? 'bg-kyck-600 font-semibold text-white hover:bg-kyck-700'
                      : inRange
                        ? 'bg-kyck-100 text-kyck-800 hover:bg-kyck-200'
                        : 'hover:bg-slate-100',
                  )}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => lastNDays(7)}
              className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50"
            >
              Last 7 days
            </button>
            <button
              type="button"
              onClick={() => lastNDays(30)}
              className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50"
            >
              Last 30 days
            </button>
            <button
              type="button"
              onClick={() =>
                preset(toIso(view.y, view.m, 1), toIso(view.y, view.m, daysInMonth(view.y, view.m)))
              }
              className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50"
            >
              This month
            </button>
            <span className="flex-1" />
            {hasValue && (
              <button
                type="button"
                onClick={() => onChange(undefined, undefined)}
                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-3 w-3" />
                Clear
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
